import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  SimpleGrid,
  Select,
  Spinner,
  HStack, VStack, Button, Input, Badge, Link, useToast, Table, Thead, Tr, Td, Tbody, Th, Checkbox
} from "@chakra-ui/react";
import { supabase } from "../../lib/supabaseClient";

function getRecentMonths(n = 24) {
  const months = [];
  const now = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    months.push(`${year}${month}`);
  }
  return months;
}

export default function AdminSettlement() {
  const [selectedMonth, setSelectedMonth] = useState(() => getRecentMonths(1)[0]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryWithoutLicensedVideo, setSummaryWithoutLicensedVideo] = useState(null);
  const [loadingWithoutLicensedVideo, setLoadingWithoutLicensedVideo] = useState(false);
  const [summaryWithoutContractor, setSummaryWithoutContractor] = useState(null);
  const [loadingWithoutContractor, setLoadingWithoutContractor] = useState(false);
  
  // 메타데이터 섹션
  const [metadataList, setMetadataList] = useState([]);
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const [metadataPage, setMetadataPage] = useState(1);
  const [metadataTotalCount, setMetadataTotalCount] = useState(0);
  const [metadataFilters, setMetadataFilters] = useState({
    channel: "",
    isrc: "",
    shortsTitle: "",
  });
  const [tempMetadataFilters, setTempMetadataFilters] = useState({
    channel: "",
    isrc: "",
    shortsTitle: "",
  });
  const [emptyChannelOnly, setEmptyChannelOnly] = useState(false);
  const [emptyShortsTitleOnly, setEmptyShortsTitleOnly] = useState(false);
  const [nonEmptyChannelOnly, setNonEmptyChannelOnly] = useState(false);
  const [nonEmptyShortsTitleOnly, setNonEmptyShortsTitleOnly] = useState(false);
  const toast = useToast();

  const [withdrawStatus, setWithdrawStatus] = useState(null);
  const [appliedAmount, setAppliedAmount] = useState(0);
  const [settlementStatus, setSettlementStatus] = useState(false);
  const [settlementLoading, setSettlementLoading] = useState(false);
  let 세금 = 0;
  let 최종정산 = 0;

  useEffect(() => {
    async function fetchSummary() {
      setLoading(true);
      const { data, error } = await supabase.rpc("get_music_payout_summary_test", { target_month: selectedMonth });
      setSummary(data && data[0] ? data[0] : null);
      setLoading(false);
    }
    fetchSummary();
  }, [selectedMonth]);

  useEffect(() => {
    async function fetchSummaryWithoutLicensedVideo() {
      setLoadingWithoutLicensedVideo(true);
      const { data, error } = await supabase.rpc("get_music_payout_summary_without_licensed_video_test", { target_month: selectedMonth });
      setSummaryWithoutLicensedVideo(data && data[0] ? data[0] : null);
      setLoadingWithoutLicensedVideo(false);
    }
    fetchSummaryWithoutLicensedVideo();
  }, [selectedMonth]);

  useEffect(() => {
    async function fetchSummaryWithoutContractor() {
      setLoadingWithoutContractor(true);
      const { data, error } = await supabase.rpc("get_music_payout_summary_without_contractor_test", { target_month: selectedMonth });
      setSummaryWithoutContractor(data && data[0] ? data[0] : null);
      setLoadingWithoutContractor(false);
    }
    fetchSummaryWithoutContractor();
  }, [selectedMonth]);

  // 정산 상태 조회
  useEffect(() => {
    async function fetchSettlementStatus() {
      const { data, error } = await supabase
        .from("settings")
        .select("bool")
        .eq("type", "정산")
        .single();
      
      if (!error && data) {
        setSettlementStatus(data.bool);
      }
    }
    fetchSettlementStatus();
  }, []);

  // 카드용 데이터 매핑
  const ownedViews = summary?.owned_views || 0;
  const revenueBase = Number(summary?.revenue_base || 0);
  const revenueVideo = Number(summary?.revenue_video || 0);
  const revenueApplied = Number(summary?.revenue_applied || 0);
  const revenuePlatform = Number(summary?.revenue_platform || 0);
  const otherSum = revenueVideo + revenueApplied + revenuePlatform;
  const diff = revenueBase - otherSum;

  // 새로운 데이터
  const withoutLicensedVideoOwnedViews = summaryWithoutLicensedVideo?.owned_views || 0;
  const withoutLicensedVideoRevenueBase = Number(summaryWithoutLicensedVideo?.revenue_base || 0);
  const withoutLicensedVideoRevenueVideo = Number(summaryWithoutLicensedVideo?.revenue_video || 0);
  const withoutLicensedVideoRevenueApplied = Number(summaryWithoutLicensedVideo?.revenue_applied || 0);
  const withoutLicensedVideoRevenuePlatform = Number(summaryWithoutLicensedVideo?.revenue_platform || 0);
  
  const withoutContractorOwnedViews = summaryWithoutContractor?.owned_views || 0;
  const withoutContractorRevenueBase = Number(summaryWithoutContractor?.revenue_base || 0);
  const withoutContractorRevenueVideo = Number(summaryWithoutContractor?.revenue_video || 0);
  const withoutContractorRevenueApplied = Number(summaryWithoutContractor?.revenue_applied || 0);
  const withoutContractorRevenuePlatform = Number(summaryWithoutContractor?.revenue_platform || 0);
  
  // 차이 계산
  const revenueDiff = withoutLicensedVideoRevenueBase - revenueBase;
  const viewsDiff = withoutLicensedVideoOwnedViews - ownedViews;

  // 메타데이터 fetch
  useEffect(() => {
    async function fetchMetadata() {
      setLoadingMetadata(true);
      const { data, error } = await supabase.rpc("get_music_payout_summary_metadata_grouped_test", {
        target_month: selectedMonth,
        p_contractor: metadataFilters.channel || null,
        p_isrc: metadataFilters.isrc || null,
        p_video_id: metadataFilters.shortsTitle || null,
        p_empty_contractor_only: emptyChannelOnly || null,
        p_empty_video_id_only: emptyShortsTitleOnly || null,
        p_non_empty_contractor_only: nonEmptyChannelOnly || null,
        p_non_empty_video_id_only: nonEmptyShortsTitleOnly || null,
        page_number: metadataPage,
      });
      
      if (error) {
        toast({ title: "메타데이터 조회 실패", description: error.message, status: "error" });
        setMetadataList([]);
        setMetadataTotalCount(0);
      } else {
        setMetadataList(data || []);
        if (data && data.length > 0 && data[0].total_count !== undefined) {
          setMetadataTotalCount(Number(data[0].total_count));
        } else {
          setMetadataTotalCount(0);
        }
      }
      setLoadingMetadata(false);
    }
    fetchMetadata();
  }, [selectedMonth, metadataFilters, metadataPage, emptyChannelOnly, emptyShortsTitleOnly, nonEmptyChannelOnly, nonEmptyShortsTitleOnly, toast]);

  // 메타데이터 검색 핸들러
  const handleMetadataSearch = () => {
    setMetadataFilters({
      channel: tempMetadataFilters.channel,
      isrc: tempMetadataFilters.isrc,
      shortsTitle: tempMetadataFilters.shortsTitle,
    });
    setMetadataPage(1);
  };

  const handleMetadataKeyDown = (e) => {
    if (e.key === "Enter") {
      handleMetadataSearch();
    }
  };

  const handleTempMetadataFilterChange = (key, value) => {
    setTempMetadataFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleCheckboxChange = (type, checked) => {
    if (type === 'emptyChannelOnly') {
      setEmptyChannelOnly(checked);
    } else if (type === 'emptyShortsTitleOnly') {
      setEmptyShortsTitleOnly(checked);
    } else if (type === 'nonEmptyChannelOnly') {
      setNonEmptyChannelOnly(checked);
    } else if (type === 'nonEmptyShortsTitleOnly') {
      setNonEmptyShortsTitleOnly(checked);
    }
  };

  // 정산 상태 토글 함수
  const handleSettlementToggle = async () => {
    setSettlementLoading(true);
    try {
      const newStatus = !settlementStatus;
      const { error } = await supabase
        .from("settings")
        .update({ bool: newStatus })
        .eq("type", "정산");
      
      if (error) {
        toast({ title: "정산 상태 변경 실패", description: error.message, status: "error" });
      } else {
        setSettlementStatus(newStatus);
        toast({ 
          title: "정산 상태 변경 완료", 
          description: newStatus ? "정산이 중단되었습니다." : "정산이 시작되었습니다.", 
          status: "success" 
        });
      }
    } catch (error) {
      toast({ title: "정산 상태 변경 실패", description: error.message, status: "error" });
    } finally {
      setSettlementLoading(false);
    }
  };

  return (
    <Box minH="100vh" bg="#f7fafd" p={6}>
      {/* 정산 시작/중단 섹션 */}
      <Box bg="white" borderRadius="2xl" p={6} boxShadow="md" mb={6}>
        <Flex justify="space-between" align="center">
          <Box>
            <Text fontWeight="bold" fontSize="xl" mb={2}>정산 관리</Text>
            <Text fontSize="sm" color="gray.500">
              현재 상태: {settlementStatus ? "정산 중단" : "정산 가능"}
            </Text>
          </Box>
          <Button
            colorScheme={settlementStatus ? "green" : "red"}
            size="md"
            onClick={handleSettlementToggle}
            isLoading={settlementLoading}
            w="120px"
          >
            {settlementStatus ? "정산 시작" : "정산 중단"}
          </Button>
        </Flex>
      </Box>
      
      <Box bg="white" borderRadius="2xl" p={6} boxShadow="md" mb={6}>
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontWeight="bold" fontSize="xl">정산 데이터</Text>
          <Flex align="center" gap={2}>
            <Text color="gray.400" fontWeight="bold">정산 월</Text>
            <Select
              value={selectedMonth}
              onChange={e => setSelectedMonth(e.target.value)}
              w="120px"
            >
              {getRecentMonths().map(month => (
                <option key={month} value={month}>{month}</option>
              ))}
            </Select>
          </Flex>
        </Flex>
        
        {loading || loadingWithoutLicensedVideo || loadingWithoutContractor ? (
          <Flex justify="center" align="center" minH="120px"><Spinner /></Flex>
        ) : (
          <SimpleGrid columns={{ base: 2, md: 6 }} spacing={4}>
            <Box>
              <Text color="gray.500" fontSize="sm">총 수익</Text>
              <Text fontWeight="bold">₩ {revenueBase.toLocaleString(undefined, {maximumFractionDigits: 4})}</Text>
              <Text fontSize="xs" color="gray.400">
                - {withoutLicensedVideoRevenueBase.toLocaleString(undefined, {maximumFractionDigits: 4})}
              </Text>
              <Text fontSize="xs" color="gray.300">
                - {withoutContractorRevenueBase.toLocaleString(undefined, {maximumFractionDigits: 4})}
              </Text>
            </Box>
            <Box>
              <Text color="gray.500" fontSize="sm">영상 수익</Text>
              <Text fontWeight="bold" color="red.500">₩ {revenueVideo.toLocaleString(undefined, {maximumFractionDigits: 4})}</Text>
              <Text fontSize="xs" color="gray.400">
                - {withoutLicensedVideoRevenueVideo.toLocaleString(undefined, {maximumFractionDigits: 4})}
              </Text>
              <Text fontSize="xs" color="gray.300">
                - {withoutContractorRevenueVideo.toLocaleString(undefined, {maximumFractionDigits: 4})}
              </Text>
            </Box>
            <Box>
              <Text color="gray.500" fontSize="sm">사용자 수익</Text>
              <Text fontWeight="bold">₩ {revenueApplied.toLocaleString(undefined, {maximumFractionDigits: 4})}</Text>
              <Text fontSize="xs" color="gray.400">
                - {withoutLicensedVideoRevenueApplied.toLocaleString(undefined, {maximumFractionDigits: 4})}
              </Text>
              <Text fontSize="xs" color="gray.300">
                - {withoutContractorRevenueApplied.toLocaleString(undefined, {maximumFractionDigits: 4})}
              </Text>
            </Box>
            <Box>
              <Text color="gray.500" fontSize="sm">플랫폼 수수료</Text>
              <Text fontWeight="bold" color="red.500">₩ {revenuePlatform.toLocaleString(undefined, {maximumFractionDigits: 4})}</Text>
              <Text fontSize="xs" color="gray.400">
                - {withoutLicensedVideoRevenuePlatform.toLocaleString(undefined, {maximumFractionDigits: 4})}
              </Text>
              <Text fontSize="xs" color="gray.300">
                - {withoutContractorRevenuePlatform.toLocaleString(undefined, {maximumFractionDigits: 4})}
              </Text>
            </Box>
            <Box>
              <Text color="gray.500" fontSize="sm">조회수</Text>
              <Text fontWeight="bold">{ownedViews.toLocaleString()}</Text>
              <Text fontSize="xs" color="gray.400">
                - {withoutLicensedVideoOwnedViews.toLocaleString()}
              </Text>
              <Text fontSize="xs" color="gray.300">
                - {withoutContractorOwnedViews.toLocaleString()}
              </Text>
            </Box>
            <Box>
              <Text color="gray.500" fontSize="sm">차이(총수익-합계)</Text>
              <Text fontWeight="bold">₩ {diff.toLocaleString(undefined, {maximumFractionDigits: 4})}</Text>
            </Box>
          </SimpleGrid>
        )}
      </Box>
      
      {/* 메타데이터 섹션 */}
      <Box bg="white" borderRadius="2xl" p={6} boxShadow="md" mb={6}>
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontWeight="bold" fontSize="xl">정산 데이터 상세</Text>
          <Flex align="center" gap={2}>
            <Text color="gray.400" fontWeight="bold">정산 월</Text>
            <Select
              value={selectedMonth}
              onChange={e => setSelectedMonth(e.target.value)}
              w="120px"
            >
              {getRecentMonths().map(month => (
                <option key={month} value={month}>{month}</option>
              ))}
            </Select>
          </Flex>
        </Flex>

        {/* 검색 영역 */}
        <Box bg="gray.50" borderRadius="xl" p={4} mb={6}>
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
            <HStack spacing={6} flexWrap="wrap">
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={1}>계약자</Text>
                <Input
                  value={tempMetadataFilters.channel}
                  onChange={e => handleTempMetadataFilterChange("channel", e.target.value)}
                  onKeyDown={handleMetadataKeyDown}
                  placeholder="계약자 입력"
                  w="200px"
                />
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={1}>ISRC</Text>
                <Input
                  value={tempMetadataFilters.isrc}
                  onChange={e => handleTempMetadataFilterChange("isrc", e.target.value)}
                  onKeyDown={handleMetadataKeyDown}
                  placeholder="ISRC 입력"
                  w="200px"
                />
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={1}>비디오 ID</Text>
                <Input
                  value={tempMetadataFilters.shortsTitle}
                  onChange={e => handleTempMetadataFilterChange("shortsTitle", e.target.value)}
                  onKeyDown={handleMetadataKeyDown}
                  placeholder="비디오 ID 입력"
                  w="200px"
                />
              </Box>
            </HStack>
            <VStack spacing={2} align="start">
              <Checkbox
                isChecked={emptyChannelOnly}
                onChange={(e) => handleCheckboxChange('emptyChannelOnly', e.target.checked)}
              >
                contractor가 비어있는 데이터 보기
              </Checkbox>
              <Checkbox
                isChecked={nonEmptyChannelOnly}
                onChange={(e) => handleCheckboxChange('nonEmptyChannelOnly', e.target.checked)}
              >
                contractor가 채워진 데이터만 보기
              </Checkbox>
              <Checkbox
                isChecked={emptyShortsTitleOnly}
                onChange={(e) => handleCheckboxChange('emptyShortsTitleOnly', e.target.checked)}
              >
                licensedVideo가 비어있는 데이터 보기
              </Checkbox>
              <Checkbox
                isChecked={nonEmptyShortsTitleOnly}
                onChange={(e) => handleCheckboxChange('nonEmptyShortsTitleOnly', e.target.checked)}
              >
                licensedVideo가 채워진 데이터만 보기
              </Checkbox>
            </VStack>
            <Button colorScheme="teal" minW="120px" h="40px" onClick={handleMetadataSearch}>검색</Button>
          </Flex>
        </Box>

        {/* 테이블 */}
        {loadingMetadata ? (
          <Flex justify="center" align="center" minH="120px"><Spinner /></Flex>
        ) : (
          <>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>채널</Th>
                  <Th>유저</Th>
                  <Th>ISRC</Th>
                  <Th>라이센스 비디오</Th>
                  <Th>조회수</Th>
                  <Th>총 수익</Th>
                  <Th>영상 수익</Th>
                  <Th>적용 수익</Th>
                  <Th>플랫폼 수수료</Th>
                </Tr>
              </Thead>
              <Tbody>
                {metadataList.length === 0 ? (
                  <Tr><Td colSpan={9} textAlign="center" color="gray.400">데이터 없음</Td></Tr>
                ) : (
                  metadataList.map((item, idx) => (
                    <Tr key={idx}>
                      <Td>
                      
                        <Text fontSize="sm">{item.channel || "-"}</Text>
                        <Text fontSize="2xs" color="gray.500">{item.video_channel_id || "-"}</Text>
                      </Td>
                      
                      <Td fontSize="xs">{item.contractor || "-"}</Td>
                      <Td fontSize="xs">{item.isrc || "-"}</Td>
                      <Td fontSize="xs">
                        <Text>{item.licensed_video || "-"}</Text>
                        <Text fontSize="2xs" color="gray.500">{item.video_id || "-"}</Text>
                      </Td>
                      <Td fontSize="xs">{Number(item.owned_views || 0).toLocaleString()}</Td>
                      <Td fontSize="xs">₩ {Number(item.revenue_base || 0).toLocaleString()}</Td>
                      <Td fontSize="xs">₩ {Number(item.revenue_video || 0).toLocaleString()}</Td>
                      <Td fontSize="xs">₩ {Number(item.revenue_applied || 0).toLocaleString()}</Td>
                      <Td fontSize="xs">₩ {Number(item.revenue_platform || 0).toLocaleString()}</Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
            
            {/* 페이지네이션 */}
            <Flex justify="center" align="center" mt={4} gap={2}>
              <Button 
                size="sm" 
                onClick={() => setMetadataPage(p => Math.max(1, p - 1))} 
                isDisabled={metadataPage === 1}
              >
                이전
              </Button>
              <Text fontWeight="bold" fontSize="sm">
                {metadataPage} / {Math.max(1, Math.ceil(metadataTotalCount / 20))}
              </Text>
              <Button 
                size="sm" 
                onClick={() => setMetadataPage(p => p + 1)} 
                isDisabled={metadataPage >= Math.ceil(metadataTotalCount / 20)}
              >
                다음
              </Button>
            </Flex>
          </>
        )}
      </Box>
    </Box>
  );
} 